import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import type { ServerEvent, ServerNotificationsEvent } from '@/types/ServerEvent';
import EventService from '@/services/EventService/EventService';
import { EventV2 } from '@/services/EventService/abstract/EventV2';
import { SERVER_SIDE_EVENT_NAME, SERVER_SIDE_EVENT_NAME_NOTIFICATIONS } from '@/services/ServerEventService/constants';
import { ServerEventCodes } from '@/services/ServerEventService/enums/ServerEventCodes';
import { ServerSideConnectionEvents, ServerSideEvents } from '@/services/ServerEventService/events';
import TokenService from '@/services/TokenService/TokenService';

class ServerEventService {
  private readonly connections: Map<string, HubConnection> = new Map();

  public static getHubConnectionUrl = (hubUrl: string) => {
    const isLocalHost = Boolean(location.port) && location.origin.includes('localhost');

    if (isLocalHost) return `${location.origin}${hubUrl}`;
    return `${import.meta.env.VITE_SSE_API_TARGET}${hubUrl}`;
  };

  public createConnection(hubUrl: string, onConnectedCallback?: () => void): HubConnection {
    const token = TokenService.getTokenData();

    if (!token) {
      throw new Error('Token is required');
    }

    if (this.connections.has(hubUrl)) {
      return this.connections.get(hubUrl)!;
    }

    const tokenFactory = () => token;

    const connection = new HubConnectionBuilder()
      .withUrl(ServerEventService.getHubConnectionUrl(hubUrl), {
        accessTokenFactory: tokenFactory,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on(SERVER_SIDE_EVENT_NAME, (payload: ServerEvent) => {
      EventService.dispatch(new ServerSideConnectionEvents.Common(payload));
    });

    connection.on(SERVER_SIDE_EVENT_NAME_NOTIFICATIONS, (payload: ServerNotificationsEvent) => {
      let notificationEvent: EventV2<ServerNotificationsEvent> | null = null;

      switch (payload.code) {
        case ServerEventCodes.CurrentNotifications:
          notificationEvent = new ServerSideEvents.CurrentNotifications(payload);
          break;
        case ServerEventCodes.NewNotifications:
          notificationEvent = new ServerSideEvents.NewNotifications(payload);
          break;
        case ServerEventCodes.ChangedNotifications:
          notificationEvent = new ServerSideEvents.ChangedNotifications(payload);
          break;
        default:
          console.error('Unexpected server notification type', payload.code);
          break;
      }

      if (notificationEvent) {
        EventService.dispatch(notificationEvent);
      }
    });

    connection
      .start()
      .then(() => {
        this.connections.set(hubUrl, connection);
        // eslint-disable-next-line no-console
        console.log('System: SSE connection is established');
      })
      .then(onConnectedCallback)
      .catch((err) => {
        console.error('System: Error while starting connection: ', err);
      });

    return connection;
  }

  public disconnectConnection(hubUrl: string) {
    const token = TokenService.getTokenData();

    if (!token) {
      throw new Error('Token is required');
    }

    const connection = this.connections.get(hubUrl);

    if (connection) {
      connection.stop();
      this.connections.delete(hubUrl);
      // eslint-disable-next-line no-console
      console.log('System: Connection disconnected');
    }
  }

  public disconnectAll() {
    this.connections.forEach((connection) => {
      connection.stop();
    });
    this.connections.clear();
    // eslint-disable-next-line no-console
    console.log(`System: All connections were disconnected`);
  }

  public getConnection(hubUrl: string): HubConnection | undefined {
    return this.connections.get(hubUrl);
  }
}

export default new ServerEventService();
